/**
 * The one sink for contract violations. Nothing is reported while the page is outdated; the
 * rest is graded — a mismatch from a server both sides keep current is a bug, anything else
 * skew — and shown where LOUD_SCHEMA_ERRORS is on, or filed to Sentry where it is off.
 */

import type { z } from 'zod';

import { sentryReporter } from '@/lib/sentry';
import { getViolationText, LOUD_SCHEMA_ERRORS_NOTE } from '@/lib/violationText';
import type { Unsubscribe } from '@/types/common';
import type {
    ContractViolation, GradedViolation, ServerGate, ViolationReporter, ViolationSource,
} from '@/types/contract';
import { ViolationKind, ViolationSeverity, ViolationSourceKind } from '@/types/contract';

import { fetchLoudSchemaErrors } from './runtimeConfig';
import { versionWatch } from './version';

// Enough to read a burst on screen; a longer list is one bug repeated.
const MAX_RAISED = 50;

type MinorListener = (violation: GradedViolation) => void;

interface ReporterDeps {
    isUiOutdated: () => Promise<boolean>;
    /** The deployment's LOUD_SCHEMA_ERRORS, read once per page. */
    isLoud: () => Promise<boolean>;
    fileQuietly: (violation: GradedViolation) => void;
}

/** Until the organization's update reports are read, no media_server counts as up to date. */
function isNoServerCurrent(): boolean {
    return false;
}

function getSourceSeverity(
    source: ViolationSource, isServerCurrent: ServerGate,
): ViolationSeverity {
    switch (source.kind) {
        case ViolationSourceKind.Cloud:
            return ViolationSeverity.Major;
        case ViolationSourceKind.Relay:
            return isServerCurrent(source.serverId)
                ? ViolationSeverity.Major
                : ViolationSeverity.Minor;
        case ViolationSourceKind.Peer:
            return ViolationSeverity.Minor;
    }
}

function getSeverity(
    violation: ContractViolation, isServerCurrent: ServerGate,
): ViolationSeverity {
    switch (violation.kind) {
        case ViolationKind.UnknownFrame:
            return ViolationSeverity.Minor;
        case ViolationKind.Mismatch:
            return getSourceSeverity(violation.source, isServerCurrent);
    }
}

/** A violation is told once per page: once per endpoint, and once per frame type. */
function getRepeatKey(violation: ContractViolation): string {
    switch (violation.kind) {
        case ViolationKind.UnknownFrame:
            return `${violation.endpoint} ${violation.frameType}`;
        case ViolationKind.Mismatch:
            return violation.endpoint;
    }
}

/**
 * What broke, whichever rows it broke in: each issue's code and field path, without the list
 * indices, so a list that drops many rows the same way is one breakage.
 */
function getBreakage(issues: readonly z.core.$ZodIssue[]): string {
    const kinds = issues.map((issue): string => {
        const fields = issue.path.filter(
            (segment): segment is string => typeof segment === 'string',
        );
        return `${issue.code} ${fields.join('.')}`;
    });
    return [...new Set(kinds)].sort().join(', ');
}

/** A violation is filed once per page for each endpoint and each kind of breakage on it. */
function getFilingKey(violation: GradedViolation): string {
    const told = `${violation.severity} ${getRepeatKey(violation)}`;
    switch (violation.kind) {
        case ViolationKind.UnknownFrame:
            return told;
        case ViolationKind.Mismatch:
            return `${told} ${getBreakage(violation.issues)}`;
    }
}

/** Grades, dedupes and routes violations; its snapshot is the major ones raised on screen. */
export class ContractReporter implements ViolationReporter {
    private readonly isUiOutdated: () => Promise<boolean>;

    private readonly isLoud: () => Promise<boolean>;

    private readonly fileQuietly: (violation: GradedViolation) => void;

    private raisedGradedViolations: readonly GradedViolation[] = [];

    private isServerCurrent: ServerGate = isNoServerCurrent;

    private loud: Promise<boolean> | null = null;

    private readonly listeners = new Set<() => void>();

    private readonly minorListeners = new Set<MinorListener>();

    private readonly toldMinor = new Set<string>();

    private readonly filedIds = new Set<string>();

    constructor({ isUiOutdated, isLoud, fileQuietly }: ReporterDeps) {
        this.isUiOutdated = isUiOutdated;
        this.isLoud = isLoud;
        this.fileQuietly = fileQuietly;
    }

    async report(violation: ContractViolation): Promise<void> {
        try {
            if (await this.isUiOutdated()) {
                return;
            }
            const severity = getSeverity(violation, this.isServerCurrent);
            const gradedValue = { ...violation, severity };
            if (!(await this.getLoud())) {
                this.fileOnce(gradedValue);
                return;
            }
            switch (gradedValue.severity) {
                case ViolationSeverity.Major:
                    this.raiseMajor(gradedValue);
                    break;
                case ViolationSeverity.Minor:
                    this.raiseMinor(gradedValue);
                    break;
            }
        } catch {
            // Reporting must not become a second failure on top of the one it reports.
        }
    }

    getSnapshot(): readonly GradedViolation[] {
        return this.raisedGradedViolations;
    }

    dismiss(): void {
        this.publish([]);
    }

    subscribe(listener: () => void): Unsubscribe {
        this.listeners.add(listener);
        return (): void => {
            this.listeners.delete(listener);
        };
    }

    subscribeMinor(listener: MinorListener): Unsubscribe {
        this.minorListeners.add(listener);
        return (): void => {
            this.minorListeners.delete(listener);
        };
    }

    setServerGate(gate: ServerGate): void {
        this.isServerCurrent = gate;
    }

    private getLoud(): Promise<boolean> {
        this.loud ??= this.isLoud();
        return this.loud;
    }

    private publish(nextGradedViolations: readonly GradedViolation[]): void {
        this.raisedGradedViolations = nextGradedViolations;
        for (const listener of [...this.listeners]) {
            listener();
        }
    }

    private fileOnce(violation: GradedViolation): void {
        const key = getFilingKey(violation);
        if (this.filedIds.has(key)) {
            return;
        }
        this.filedIds.add(key);
        this.fileQuietly(violation);
    }

    private raiseMajor(violation: GradedViolation): void {
        console.error(
            `Contract violation: ${getViolationText(violation)}\n${LOUD_SCHEMA_ERRORS_NOTE}`,
            violation,
        );
        this.publish([...this.raisedGradedViolations, violation].slice(-MAX_RAISED));
    }

    private raiseMinor(violation: GradedViolation): void {
        const key = getRepeatKey(violation);
        if (this.toldMinor.has(key)) {
            return;
        }
        this.toldMinor.add(key);
        console.warn(
            `Minor contract violation: ${getViolationText(violation)}\n${LOUD_SCHEMA_ERRORS_NOTE}`,
            violation,
        );
        for (const listener of [...this.minorListeners]) {
            listener(violation);
        }
    }
}

export const contractReporter: ViolationReporter = new ContractReporter({
    isUiOutdated: (): Promise<boolean> => versionWatch.check(),
    isLoud: fetchLoudSchemaErrors,
    fileQuietly: (violation: GradedViolation): void => {
        sentryReporter.reportContractViolation(violation);
    },
});
